import {
    AppUser,
    EventrDate,
    IEventrActivity,
    INextComponent,
    IOptionsWithTypes,
    ITodoList,
    TZoneTypes,
    V2,
    checkClaimsWeb,
    getV2ValueWP,
    isDateInPast,
    isUserPartOfUsersWithTags,
    spError,
    spErrorTags,
    spLog,
    structuredCloneL,
    tryParseJSON,
    useItinInfo,
} from '@eventr/common'
import { parseISO } from 'date-fns'
import isPast from 'date-fns/isPast'
import { cloneDeep } from 'lodash'
import { createSelector } from 'reselect'
import { StateCreator } from 'zustand'
import { shallow } from 'zustand/shallow'
import { DataAPI } from '../../data/Data'
import { useGuestStore } from '../../feat/guest/guest.store'
import {
    filterOutPendingTag,
    getDelayPromise,
    getTimezoneOfItinerary,
    updateSection,
} from '../../utils'
import { TodoRange } from '../../v2/componentCards/todoCard'
import { ScreenType } from '../../v2/renderers/rendererLocator'
import { generateUpNextList } from '../../v2/upNextRenderer'
import {
    categoriesActivities,
    getDataArray,
    splitImportedData,
    splitTodosData,
} from '../../v2/v2Utils'
import { BoundStore, bs, useBoundStore } from '../bound.store'
import { IItinerary } from '../interfaces'
import { useLanguageContext } from '../languageState/languageContext'
import { defaultAddSnack } from '../overlayState/overlayContext'
import { loadingSignal } from '../overlayState/overlaySignals'
import { useEphemeralStore } from '../useEphemeralStore'
import { userPrefShowDestTime } from '../userPrefsState/userPrefsSignels'
import {
    CurrentItinSlice,
    defaultCurrentItinSlice,
} from './currentItin.interfaces'
import { getItineraryMapByID, replaceRenderOptions } from './itineraryMap.slice'

export const createCurrentItinSlice: StateCreator<
    BoundStore,
    [['zustand/devtools', never]],
    [],
    CurrentItinSlice
> = (set, get) => ({
    ...defaultCurrentItinSlice,
    setCurrentItin: async (id: number) => {
        const { getItineraryByID } = get()
        useEphemeralStore.setState({
            selectedLngLatLabelledItineraryComponents: null,
            selectedLngLatLabelledTeamMember: null,
            mapDrawerIsPresented: false,
        }) // Ensure this closing parenthesis matches an opening one above

        // selectedItinCode.value = getSelectedItinCode()
        const mapEntry = getItineraryMapByID(id)
        if (!mapEntry) {
            if (id === -1) {
                set(
                    { selectedItinerary: undefined, selectedItinID: id },
                    false,
                    'setCurrentItin'
                )
            } else {
                spError('Selected itinerary not found in map')
            }
            return
        }

        const localItineraryData = getItineraryByID(id)

        if (!localItineraryData) {
            spLog('Selected itinerary not found in local store, fetch it', id)
            const eventData = await bs().pullEventData(id)
            if (eventData) {
                set(
                    {
                        selectedItinerary: replaceRenderOptions(eventData),
                        selectedItinID: id,
                    },
                    false,
                    'setCurrentItin-localNotFound'
                )
            }
            return
        } else {
            if (
                localItineraryData.version.toString() !==
                mapEntry.itinerary_version.toString()
            ) {
                spLog('Need to update local version ')
                const eventData = await bs().pullEventData(id)
                if (eventData) {
                    set(
                        {
                            selectedItinerary: replaceRenderOptions(eventData),
                            selectedItinID: id,
                        },
                        false,
                        'setCurrentItin - version update'
                    )
                }
            } else {
                set(
                    {
                        selectedItinerary:
                            replaceRenderOptions(localItineraryData),
                        selectedItinID: id,
                    },
                    false,
                    'setCurrentItin'
                )
            }
        }
    },
    setPast: (past) => {
        set({ past }, false, 'setPast')
    },
    getEventInfoV2: () => {
        if (get().selectedItinerary) {
            return get().selectedItinerary?.v2?.find((d) => {
                return d.sectionName === 'event_information'
            })
        }
        return undefined
    },
    getCurrentEventImageUrl: () => {
        const eventInfo = get().getEventInfoV2()
        if (eventInfo) {
            const imageUrl = eventInfo?.data?.cover_image
            return imageUrl
        }
        return undefined
    },
    filterEntitiesUserSpecific: (
        screenType: ScreenType,
        data: V2,
        userList?: string
    ) => {
        const { isUserPartOfSelection, override, getUserFromID } = get()
        const visibilityPath = (data.rendererOptions?.settings as any)
            ?.visibilityPath

        const arrayData: any[] = getDataArray(data)

        const filteredData: any[] = []
        // this is a hacky way to beable to use 2 lists of users, maybe rework to be x lists
        let userListPath = []
        let userListPath2: string[] = []
        if (userList?.includes('&123&')) {
            userListPath = userList.split('&123&')[0].split('.')
            userListPath2 = userList.split('&123&')[1].split('.')
        } else {
            userListPath = userList?.split('.') || []
        }
        //const userListPath = userList?.split('.')
        if (screenType === 'personal' && userList) {
            for (let i = 0; i < arrayData.length; i++) {
                const e = arrayData[i]
                let ignore = false
                if (visibilityPath) {
                    getV2ValueWP(visibilityPath, e) === 'Public'
                        ? (ignore = true)
                        : (ignore = false)
                }

                if (
                    ignore ||
                    isUserPartOfSelection(
                        getAllUsersCl(userListPath ?? [], e)
                    ) ||
                    isUserPartOfSelection(getAllUsersCl(userListPath2 ?? [], e))
                ) {
                    filteredData.push(e)
                }
            }
        } else if (screenType === 'teamMember' && userList) {
            for (let i = 0; i < arrayData.length; i++) {
                const e = arrayData[i]
                let ignore = false
                if (visibilityPath) {
                    getV2ValueWP(visibilityPath, e) === 'Public'
                        ? (ignore = true)
                        : (ignore = false)
                }
                if (override) {
                    const user = getUserFromID(override)
                    const users = getAllUsersCl(userListPath ?? [], e)
                    if (
                        ignore ||
                        (user && isUserPartOfUsersWithTags(user, users)) ||
                        isUserPartOfSelection(
                            getAllUsersCl(userListPath2 ?? [], e)
                        )
                    ) {
                        filteredData.push(e)
                    }
                }
            }
        } else {
            try {
                filteredData.push(...(arrayData || []))
            } catch (error) {
                spErrorTags(error, ['DataProvider'], arrayData)
            }
        }
        return filteredData
    },
    getSectionV2FromSectionId: (sectionId: number) => {
        if (get().selectedItinerary) {
            return get().selectedItinerary?.v2?.find((d) => {
                return d.sectionID === sectionId
            })
        }
        return undefined
    },
    updateItinerarySection: async (sectionId: number, newSectionData: any) => {
        const { selectedItinerary, forceItineraryMapUpdate, selectedItinID } =
            get()
        loadingSignal.value = true
        try {
            if (!newSectionData || !selectedItinID) {
                defaultAddSnack('Bad data or no selected itinerary.', 'error')
                loadingSignal.value = false
                return false
            }
            if (sectionId === 2) {
                //Flight section
                const flight = cloneDeep(newSectionData)
                const originalCheckIn = flight.check_in
                const x = new EventrDate(flight.departure.dep_time)
                x.subtract({ hours: 2 })
                const computedCheckIn = x.dateTimeString
                if (originalCheckIn !== computedCheckIn) {
                    newSectionData = {
                        ...newSectionData,
                        check_in: computedCheckIn,
                    }
                }
            }
            if (sectionId !== 1) {
                await DataAPI.updateSection(
                    selectedItinID,
                    sectionId.toString(),
                    {
                        comparison: '_id',
                        [sectionId.toString()]: { data: [newSectionData] },
                    }
                )
                const newSection = updateSection(
                    selectedItinerary ?? ({} as IItinerary),
                    newSectionData,
                    sectionId
                )
                set(
                    { selectedItinerary: newSection },
                    false,
                    'updateItinerarySection'
                )
            } else {
                await DataAPI.createUpdateGeneralInfo({
                    itinerary_id: selectedItinID,
                    section_id: 1,
                    value: newSectionData,
                })
            }
            await get().pullEditValues()
            await forceItineraryMapUpdate()
        } catch (error) {
            spErrorTags(error, ['DataProvider'])
            return false
        } finally {
            loadingSignal.value = false
        }
        return true
    },
    deleteItineraryItem: async (sectionId: number, itemId: string) => {
        const { forceItineraryMapUpdate, selectedItinID } = get()
        if (sectionId && selectedItinID) {
            await DataAPI.deleteBooking(
                itemId,
                sectionId.toString(),
                selectedItinID
            )
            await forceItineraryMapUpdate()
            return true
        } else {
            defaultAddSnack('Bad data or no selected itinerary.', 'error')
            return false
        }
    },
    getV2Value: (valueLocator: string, data: any) => {
        const { getCurrentUser } = get()
        if (!valueLocator) return ''
        return getV2ValueWP(valueLocator, data, getCurrentUser())
    },
    getSectionV2FromSectionIdFromSelectedItinerary: (sectionId: number) => {
        const { selectedItinerary } = get()
        if (selectedItinerary && selectedItinerary.v2) {
            const tmpV2 = selectedItinerary.v2.find((v2Data) => {
                return v2Data.sectionID === sectionId
            })
            if (tmpV2) {
                return tmpV2
            } else {
                return undefined
            }
        } else {
            return undefined
        }
    },
    getApiUpdates: async () => {
        const flightSection = get().selectedItinerary?.v2?.find(
            (d) => d.sectionID === 2
        )
        const flights = flightSection?.data?.flights ?? []

        const flightsList = flights.map((f) => {
            return {
                flight_id: f.flight_id,
                flight_version: f.flight_version,
                _id: f._id,
            }
        })

        const updates = await DataAPI.getAPIUpdates(
            flightsList,
            get().selectedItinID
        )
        if ((updates.data?.original?.length ?? 0) > 0) {
            await updates.data?.original?.forEach(async (update) => {
                const selectedOrgName = get().selectedOrg?.name
                if (selectedOrgName) {
                    await DataAPI.guest.duplicateItinerary(selectedOrgName, {
                        code: update.codeInfo.code,
                        itin_to_copy_to: bs().selectedItinID,
                    })
                }
            })
            await bs().forceItineraryMapUpdate()
        }
        set({ apiUpdates: updates.data ?? {} }, false, 'getApiUpdates')
    },
    removePublicItinerary: async (itinCode: string) => {
        const { selectedItinID, forceItineraryMapUpdate } = get()
        loadingSignal.value = true
        try {
            await DataAPI.itineraries.removePublicItin(selectedItinID, itinCode)
            await forceItineraryMapUpdate()
        } catch (error) {
            spErrorTags(error, ['DataProvider'])
        } finally {
            loadingSignal.value = false
        }
    },
    updateTodo: async (taskId: string, status: boolean, fillerId?: number) => {
        const { selectedItinID } = get()
        const stringStatus = status ? 'completed' : 'incomplete'
        const delayPromise = getDelayPromise(1000)

        const [resp] = await Promise.all([
            DataAPI.todo.upsertTodo({
                filler: { status: stringStatus },
                object_id: taskId,
                filler_id: fillerId,
                itinerary_id: selectedItinID,
            }),
            delayPromise,
        ])
        if (resp.status === 'success') {
            set(
                {
                    apiUpdates: {
                        ...get().apiUpdates,
                        todos: {
                            ...(get().apiUpdates?.todos ?? {}),
                            [taskId]: {
                                id: resp.data.id,
                                value: {
                                    status: stringStatus,
                                    user_id: resp.data.created_by,
                                },
                            },
                        },
                    },
                },
                false,
                'updateTodo'
            )
        }
    },
    pullEditValues: async () => {
        try {
            const { selectedItinID } = get()
            if (selectedItinID === -1) return
            if (!checkClaimsWeb([`/${selectedItinID}`], ['editItinerary'])) {
                set(
                    { editValues: undefined },
                    false,
                    'pullEditValuesNotAllowed'
                )
                return
            }
            const resp = await DataAPI.editValues.getAll(selectedItinID)
            if (resp.status === 'success') {
                set({ editValues: resp.data }, false, 'pullEditValues')
            }
        } catch (error) {
            spErrorTags(error, ['DataProvider'])
            set({ editValues: undefined }, false, 'pullEditValuesError')
        }
    },
    upsertElement: async (sectionID: number, element: any) => {
        const { selectedItinID, forceItineraryMapUpdate } = get()
        const resp = await DataAPI.editValues.upsertElement(
            selectedItinID,
            sectionID,
            element
        )
        if (resp.status !== 'success') {
            spError('Error upserting element')
            return resp
        } else {
            await get().pullEditValues()
            await forceItineraryMapUpdate()
        }
        return resp
    },
    //this one is used for creating a new element in the state only
    insertEditItem: (sectionID: number, item: any) => {
        const { editValues } = get()
        if (editValues) {
            const section =
                editValues[sectionID as unknown as keyof typeof editValues]
            if (section) {
                const activities = section.activities
                if (activities) {
                    activities.push(item)
                    set(
                        { editValues: { ...editValues, [sectionID]: section } },
                        false,
                        'insertEditItem'
                    )
                }
            }
        }
    },
    deleteElement: async (sectionID: number, itemId?: string) => {
        const { selectedItinID, forceItineraryMapUpdate } = get()
        const resp = await DataAPI.editValues.deleteElement(
            selectedItinID,
            sectionID,
            itemId
        )
        if (resp.status !== 'success') {
            spError('Error deleting element')
            return resp
        } else {
            await get().pullEditValues()
            await forceItineraryMapUpdate()
        }
        return resp
    },
    setLastUsedDateTime: (dateTime: string) => {
        set({ lastUsedDateTime: dateTime }, false, 'setLastUsedDateTime')
    },
})

// MARK: Subscriptions

setTimeout(() => {
    useBoundStore.subscribe(
        (state) => ({
            itin: state.selectedItinerary,
            refreshKey: state.refreshKey,
        }),
        ({ itin, refreshKey }) => {
            spLog('selectedItin subscription called', refreshKey)

            let eventEnded = false
            if (itin && itin.v2?.[0].data.end_date) {
                eventEnded = isDateInPast(itin.v2[0].data.end_date)
            }
            useBoundStore.setState({ eventEnded: eventEnded })
            // set itin timezone
            if (itin) {
                useItinInfo.getState().setTimezone(getTimezoneOfItinerary(itin))
            }
            const user = bs().wpUser
            if (itin) {
                generateItineraryComponents(
                    itin,
                    userPrefShowDestTime.value,
                    user
                )
            }
        },
        { fireImmediately: true, equalityFn: shallow }
    )
    useBoundStore.subscribe(
        (state) => ({
            itinID: state.selectedItinID,
            refreshKey: state.refreshKey,
        }),
        ({ itinID, refreshKey }) => {
            spLog('selectedItinID subscription called', itinID, refreshKey)

            bs().pullEditValues()
            bs().getApiUpdates()
            bs().pullRocketChatChannels()
        },
        { fireImmediately: true, equalityFn: shallow }
    )
    useBoundStore.subscribe(
        (state: BoundStore) => state.itineraryComponents,
        (state) => {
            spLog('itineraryComponents subscription called ')
            setSelectedItinEmpty(state)
        },
        { fireImmediately: true, equalityFn: shallow }
    )
}, 100)

// MARK: Selectors

export const selectPendingTodoCount = createSelector(
    (state: BoundStore) => state.apiUpdates?.todos,
    (state: BoundStore, todoList: ITodoList) => todoList,
    (todoStatus, todoList: ITodoList) => {
        if (todoStatus && todoList) {
            const pendingTodos = (todoList.tasks ?? []).filter((todo) => {
                return (
                    todoStatus[todo._id] === undefined ||
                    todoStatus[todo._id].value.status === 'incomplete'
                )
            })
            return pendingTodos.length
        }
        return 0
    }
)

export const selectAllEditTodos = createSelector(
    (state: BoundStore) => state.editValues?.[8],
    (activities) => {
        return (
            activities?.activities.filter(
                (activity) => activity.type === 'todo'
            ) ?? []
        )
    }
)
export const selectAllEditGeneralActivities = createSelector(
    (state: BoundStore) => state.editValues?.[8],
    (activities) => {
        return (
            activities?.activities.filter(
                (activity) =>
                    activity.type !== 'todo' && activity._og_code === undefined
            ) ?? []
        )
    }
)
export const selectCurrentItineraryTitle = createSelector(
    (state: BoundStore) => state.selectedItinerary,
    (itin) => {
        return itin?.v2?.find((s) => s.sectionID === 1)?.data?.title ?? ''
    }
)

export const selectEditItem = createSelector(
    (state: BoundStore) => state.editValues,
    (state: BoundStore, sectionID: number, _id?: string) => sectionID,
    (state: BoundStore, sectionID: number, _id?: string) => _id,
    (editValues, sectionID: number, _id?: string) => {
        if (sectionID) {
            const section =
                editValues?.[sectionID as unknown as keyof typeof editValues]

            if (section) {
                if (sectionID === 8) {
                    return (
                        section.activities?.find(
                            (activity: { _id: string }) => activity._id === _id
                        ) ?? null
                    )
                }
                if (sectionID === 1) {
                    return structuredCloneL(section)
                }
            }
        }
        return null
    }
)

export const selectAllPublishedTodos = createSelector(
    (state: BoundStore) => state.selectedItinerary,
    (itin) => {
        const todos: IEventrActivity[] =
            itin?.v2
                ?.find((s) => s.sectionID === 8)
                ?.data?.activities?.filter(
                    (a: IEventrActivity) => a.type === 'todo'
                ) ?? []

        return todos ?? []
    }
)

export const selectFilterNonItineraryUsers = createSelector(
    (state: BoundStore) => state.selectedItinerary,
    (state: BoundStore, userList: IOptionsWithTypes[]) => userList,
    (itin, userList) => {
        const itinMembers: IOptionsWithTypes[] = itin?.v2?.find(
            (s) => s.sectionID === 1
        )?.data?.members
        const filteredMembers = userList?.filter((u) => {
            return itinMembers?.some((m) => m.option === u.option)
        })
        return filteredMembers ?? []
    }
)

export const weekInMillis = 7 * 24 * 60 * 60 * 1000
export const threeDaysInMillis = 3 * 24 * 60 * 60 * 1000
export const selectTodoListRange = createSelector(
    (state: BoundStore) => state.apiUpdates?.todos,
    (state: BoundStore, todoList: ITodoList[]) => todoList,
    (todoStatus, todoList: ITodoList[]) => {
        if (!todoList || !Array.isArray(todoList) || todoList.length === 0) {
            return 'normal' as TodoRange
        }

        // Start with the best status
        let worstStatus: TodoRange = 'normal'

        // Check if all todos are completed
        const allCompleted = todoList.every((todo) => {
            // Check if todo has tasks and all tasks are completed
            if (todo.tasks && todo.tasks.length > 0) {
                return todo.tasks.every(
                    (task) =>
                        todoStatus?.[task._id]?.value?.status === 'completed'
                )
            }
            return true // Consider a todo with no tasks as completed
        })

        if (allCompleted) {
            return 'done' as TodoRange
        }
        const now = new Date().getTime()
        // Loop through each todo to find the worst status
        for (const todo of todoList) {
            if (!todo.due_date && todo.start_by) {
                const start_by = new EventrDate(todo.start_by)
                // Check if this todo is in warning zone (less than start_by)
                if (start_by.getUnixMs - now < 0) {
                    // Set to warning, but keep checking for possible danger status
                    worstStatus = 'warning'
                }
            }
            // Skip todos without due dates
            if (!todo.due_date) continue

            const date = new EventrDate(todo.due_date)
            const start_by = new EventrDate(todo.start_by ?? todo.due_date)
            const diff = date.getUnixMs - now

            const pendingTodos = todo.tasks.filter((todo) => {
                return (
                    todoStatus?.[todo._id] === undefined ||
                    todoStatus?.[todo._id]?.value?.status === 'incomplete'
                )
            })
            if (pendingTodos.length === 0) continue

            // Check if this todo is in danger zone (past due date)
            if (diff < 0) {
                // If we find a danger status, we can return early since it's the worst
                return 'danger' as TodoRange
            }

            // Check if this todo is in warning zone (less than start_by)
            if (start_by.getUnixMs - now < 0) {
                // Set to warning, but keep checking for possible danger status
                worstStatus = 'warning'
            }
        }

        return worstStatus
    }
)
export const selectorSplitData = createSelector(
    (state: BoundStore) => state.selectedItinerary?.v2,
    (state: BoundStore) => state.orgConfig?.activityCategories,
    // dataV2 is optional if we want to some other data
    (state: BoundStore, dataV2?: V2[]) => dataV2,
    (data, categories, dataV2) => {
        const dataToUse = dataV2 ?? data

        if (!dataToUse) return { splitSections: [], categorized: [], todos: [] }
        const { splitSections, notImported } = splitImportedData(
            dataToUse,
            categories
        )
        const { allOtherData, todos } = splitTodosData(notImported)

        const categorized = categoriesActivities(allOtherData, categories)

        return { splitSections, categorized, todos }
    }
)
export const selectCategoryActivities = createSelector(
    (state: BoundStore, categoryID?: string, ogCode?: string, dataV2?: V2[]) =>
        selectorSplitData(state, dataV2),
    (state: BoundStore, categoryID?: string, ogCode?: string) => categoryID,
    (state: BoundStore, categoryID?: string, ogCode?: string) => ogCode,
    ({ categorized, splitSections }, categoryID, ogCode) => {
        const mainCat = categorized.find((d) => {
            if (ogCode) {
                return d.category === categoryID && d.ogCode === ogCode
            }
            return d.category === categoryID
        })

        if (mainCat) {
            return mainCat
        }
        if (splitSections) {
            for (const s of splitSections) {
                const data = s.data.find((d) => {
                    if (ogCode) {
                        return d.category === categoryID && d.ogCode === ogCode
                    }
                    return d.category === categoryID
                })
                if (data) {
                    return data
                }
            }
        }
        return mainCat
    }
)

export const selectIsItineraryEmpty = createSelector(
    (state: BoundStore) => state.itineraryComponents,
    (state: BoundStore) => state.getEventInfoV2(),
    (state: BoundStore) => selectorSplitData(state),
    (itineraryComponents, eventInfo) => {
        if (!itineraryComponents || !itineraryComponents.present) {
            return undefined // Or potentially true/false depending on desired default behavior
        }
        if (Object.keys(itineraryComponents.present).length === 0) {
            if (
                eventInfo?.data?.end_date &&
                isPast(parseISO(eventInfo.data.end_date))
            )
                return true
        } else {
            if (
                Object.keys(itineraryComponents.present).length === 1 &&
                Object.keys(itineraryComponents.present)[0] === 'Other' &&
                eventInfo?.data?.end_date &&
                isPast(parseISO(eventInfo.data.end_date))
            ) {
                return true
            } else {
                return false
            }
        }
    }
)

//MARK: Helpers
const getAllUsersCl = (path: string[], d: any): any[] => {
    return tryParseJSON(getV2ValueWP(path.join('.'), d), [])
}

export const transformItineraryComponents = (
    itin: IItinerary,
    timeType: TZoneTypes,
    appUser: AppUser
) => {
    const organisedDataRenderArray = itin?.v2?.slice(1).concat(itin?.v2?.[0])
    let user = null
    if (useGuestStore.getState().isGuestMode) {
        user = { id: -1 }
    } else {
        user = appUser
    }

    if (user) {
        const comps: INextComponent[] = []
        const pastComps: INextComponent[] = []
        const todos: any[] = []
        const onlyUpNextData = (organisedDataRenderArray || []).filter((d) => {
            return d.rendererOptions.settings.displayInUpNext
        })

        onlyUpNextData.forEach((sectionData) => {
            const filteredByUser = useBoundStore
                .getState()
                .filterEntitiesUserSpecific(
                    'personal',
                    sectionData,
                    sectionData.rendererOptions.settings.userList
                )

            /// Remove pending items for itinerary page
            const filteredOutPending =
                filteredByUser.filter(filterOutPendingTag)

            const upNextSectionList = generateUpNextList(
                user,
                filteredOutPending,
                sectionData.rendererOptions,
                'upNext',
                timeType,
                sectionData,
                useLanguageContext.getState().timeFormat
            )

            if (upNextSectionList) {
                comps.push(...upNextSectionList.present)
                pastComps.push(...upNextSectionList.past)
                todos.push(...upNextSectionList.todos)
            }
        })

        comps.forEach((entry) => {
            const start = entry.data?.start
            const end = entry.data?.end

            if (start === end) {
                entry.data.PIT = true
            }
        })

        comps.sort((a, b) => {
            const diff =
                new EventrDate(a.date === '' ? undefined : a.date).getUnixMs -
                new EventrDate(b.date === '' ? undefined : b.date).getUnixMs
            return diff ? diff : 1
        })
        pastComps.sort((a, b) => {
            const diff =
                new EventrDate(a.date === '' ? undefined : a.date).getUnixMs -
                new EventrDate(b.date === '' ? undefined : b.date).getUnixMs
            return diff ? diff : 1
        })

        const groupPerDate = (list: INextComponent[]) => {
            const groupedData: { [key: string]: INextComponent[] } = {}
            list.forEach((item) => {
                if (item.date) {
                    const date = item.date.split(' ')[0]
                    if (!groupedData[date]) {
                        groupedData[date] = []
                    }
                    groupedData[date].push(item)
                } else {
                    if (!groupedData['Other']) {
                        groupedData['Other'] = []
                    }
                    groupedData['Other'].push(item)
                }
            })
            return groupedData
        }

        const presentObj = groupPerDate(comps)
        const pastObj = groupPerDate(pastComps)

        const itineraryComponents = {
            present: presentObj,
            past: pastObj,
            todos: todos,
        }

        return itineraryComponents
    }
}

const generateItineraryComponents = (
    itin: IItinerary,
    timeType: TZoneTypes,
    appUser?: AppUser
) => {
    const organisedDataRenderArray = itin?.v2?.slice(1).concat(itin?.v2?.[0])
    let user = null
    if (useGuestStore.getState().isGuestMode) {
        user = { id: -1 }
    } else {
        user = appUser
    }

    if (user) {
        const comps: INextComponent[] = []
        const pastComps: INextComponent[] = []
        const todos: any[] = []
        const onlyUpNextData = (organisedDataRenderArray || []).filter((d) => {
            return d.rendererOptions.settings.displayInUpNext
        })

        onlyUpNextData.forEach((sectionData) => {
            const filteredByUser = useBoundStore
                .getState()
                .filterEntitiesUserSpecific(
                    'personal',
                    sectionData,
                    sectionData.rendererOptions.settings.userList
                )

            /// Remove pending items for itinerary page
            const filteredOutPending =
                filteredByUser.filter(filterOutPendingTag)

            const upNextSectionList = generateUpNextList(
                user,
                filteredOutPending,
                sectionData.rendererOptions,
                'upNext',
                timeType,
                sectionData,
                useLanguageContext.getState().timeFormat
            )

            if (upNextSectionList) {
                comps.push(...upNextSectionList.present)
                pastComps.push(...upNextSectionList.past)
                todos.push(...upNextSectionList.todos)
            }
        })

        comps.sort((a, b) => {
            const diff =
                new EventrDate(a.date === '' ? undefined : a.date).getUnixMs -
                new EventrDate(b.date === '' ? undefined : b.date).getUnixMs
            return diff ? diff : 1
        })
        pastComps.sort((a, b) => {
            const diff =
                new EventrDate(a.date === '' ? undefined : a.date).getUnixMs -
                new EventrDate(b.date === '' ? undefined : b.date).getUnixMs
            return diff ? diff : 1
        })

        const groupPerDate = (list: INextComponent[]) => {
            const groupedData: { [key: string]: INextComponent[] } = {}

            list.forEach((item) => {
                if (item.sortDate) {
                    const date = item.sortDate.split(/[OT]/)[0]
                    if (!groupedData[date]) {
                        groupedData[date] = []
                    }
                    groupedData[date].push(item)
                } else {
                    if (!groupedData['Other']) {
                        groupedData['Other'] = []
                    }
                    groupedData['Other'].push(item)
                }
            })

            return groupedData
        }

        const presentObj = groupPerDate(comps)
        const pastObj = groupPerDate(pastComps)

        const itineraryComponents = {
            present: presentObj,
            past: pastObj,
            todos: todos,
        }

        useBoundStore.setState(
            {
                itineraryComponents,
            },
            false,
            'generateItineraryComponents'
        )
    }
}

/**
 * @deprecated use selectIsItineraryEmpty
 */
const setSelectedItinEmpty = (itineraryComponents: any) => {
    if (!itineraryComponents || !itineraryComponents.present) {
        useBoundStore.setState(
            { selectedItinEmpty: undefined },
            false,
            'setSelectedItinEmpty'
        )
        return
    }
    if (Object.keys(itineraryComponents.present).length === 0) {
        useBoundStore.setState(
            { selectedItinEmpty: true },
            false,
            'setSelectedItinEmpty'
        )
    } else {
        if (
            Object.keys(itineraryComponents.present).length === 1 &&
            Object.keys(itineraryComponents.present)[0] === 'Other' &&
            isPast(parseISO(bs().getEventInfoV2()?.data.end_date))
        ) {
            useBoundStore.setState(
                { selectedItinEmpty: true },
                false,
                'setSelectedItinEmpty'
            )
        } else {
            useBoundStore.setState(
                { selectedItinEmpty: false },
                false,
                'setSelectedItinEmpty'
            )
        }
    }
}
